import React, { useReducer, useEffect, useRef, useState, ReactNode } from "react";
import { useContextSelector, createContext } from "use-context-selector";

import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import { bankKey, liftKey, BankKey, LiftKey, bitmaskPrefix } from "../../../utils/typedKeys";
import { bitmaskToDirectionIcon } from "../../../utils/bitmaskToIcon";

interface State {
  byBank: { [key: BankKey]: SocketMessageInterface };
  byLift: { [key: LiftKey]: SocketMessageInterface };
  bitmask: { [key: string]: boolean };
  positionMap: { [key: string]: number };
  serviceMap: { [key: string]: string };
  debounceMap: Map<string, number>;
  last?: SocketMessageInterface;
}

type Action =
  | { type: "UPDATE_MESSAGE"; payload: SocketMessageInterface }
  | { type: "UPDATE_DEBOUNCE_MAP"; payload: Map<string, number> }
  | { type: "CLEAR_MESSAGES" };

interface DirectionEntry {
  directionIcon: string | null;
  lastDirectionIcon: string | null;
}

const BankMessagesContext = createContext<State | undefined>(undefined);
const BankMessagesDispatch = createContext<React.Dispatch<Action> | undefined>(
  undefined
);
const DirectionContext = createContext<{ [key: string]: DirectionEntry } | undefined>(undefined);

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "UPDATE_MESSAGE": {
      const msg = action.payload;
      const bk = bankKey(msg.bankId);
      const lk = liftKey(msg.liftId);
      return {
        byBank: { ...state.byBank, [bk]: msg },
        byLift: { ...state.byLift, [lk]: msg },
        bitmask: { ...state.bitmask, ...msg.bitmask },
        positionMap: { ...state.positionMap, ...msg.positionMap },
        serviceMap: { ...state.serviceMap, ...msg.serviceMap },
        debounceMap: state.debounceMap,
        last: msg,
      };
    }
    case "UPDATE_DEBOUNCE_MAP": {
      return { ...state, debounceMap: action.payload };
    }
    case "CLEAR_MESSAGES": {
      return {
        byBank: {},
        byLift: {},
        bitmask: {},
        positionMap: {},
        serviceMap: {},
        debounceMap: state.debounceMap,
        last: undefined,
      };
    }
    default:
      return state;
  }
}

export const BankMessagesProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, {
    byBank: {},
    byLift: {},
    bitmask: {},
    positionMap: {},
    serviceMap: {},
    debounceMap: new Map(),
    last: undefined,
  });

  const [directionByLift, setDirectionByLift] = useState<{ [key: string]: DirectionEntry }>({});
  const debouncedDirRef = useRef<{ [key: string]: string | null }>({});
  const lastNonNullDirRef = useRef<{ [key: string]: string | null }>({});
  const timersRef = useRef<{ [key: string]: ReturnType<typeof setTimeout> }>({});

  useEffect(() => {
    if (!state.last) return;
    const msg = state.last;
    const lk = liftKey(msg.liftId);
    const prefix = bitmaskPrefix(msg.bankId, msg.liftId);
    const rawDirection = bitmaskToDirectionIcon(msg.bitmask, prefix);
    const currentDebounced = debouncedDirRef.current[lk] ?? null;

    if (rawDirection === currentDebounced) return;

    let debounceMs = 0;
    if (rawDirection === "elUp") debounceMs = state.debounceMap.get("ELEVATOR:0") ?? 0;
    else if (rawDirection === "elDown") debounceMs = state.debounceMap.get("ELEVATOR:1") ?? 0;

    if (timersRef.current[lk]) {
      clearTimeout(timersRef.current[lk]);
      delete timersRef.current[lk];
    }

    const apply = () => {
      debouncedDirRef.current[lk] = rawDirection;
      if (rawDirection) lastNonNullDirRef.current[lk] = rawDirection;
      const lastDir = lastNonNullDirRef.current[lk] ?? null;
      setDirectionByLift((prev) => {
        const next: DirectionEntry = {
          directionIcon: rawDirection,
          lastDirectionIcon: rawDirection ?? lastDir,
        };
        if (
          prev[lk]?.directionIcon === next.directionIcon &&
          prev[lk]?.lastDirectionIcon === next.lastDirectionIcon
        ) return prev;
        return { ...prev, [lk]: next };
      });
    };

    if (debounceMs <= 0) {
      apply();
    } else {
      timersRef.current[lk] = setTimeout(() => {
        delete timersRef.current[lk];
        apply();
      }, debounceMs);
    }
  }, [state.last, state.debounceMap]);

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach((timer) => clearTimeout(timer));
    },
    [],
  );

  return (
    <BankMessagesContext.Provider value={state}>
      <BankMessagesDispatch.Provider value={dispatch}>
        <DirectionContext.Provider value={directionByLift}>
          {children}
        </DirectionContext.Provider>
      </BankMessagesDispatch.Provider>
    </BankMessagesContext.Provider>
  );
};

export function useLastBankMessage(
  bankId: UuidT
): SocketMessageInterface | undefined {
  return useContextSelector(
    BankMessagesContext,
    (ctx) => ctx?.byBank[bankKey(bankId)]
  );
}

export function useLastLiftMessage(
  liftId: UuidT
): SocketMessageInterface | undefined {
  return useContextSelector(
    BankMessagesContext,
    (ctx) => ctx?.byLift[liftKey(liftId)]
  );
}

export function useAllLiftMessages(): {
  [key: string]: SocketMessageInterface;
} {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.byLift) || {};
}

export function useLastSocketMessage(): SocketMessageInterface | undefined {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.last);
}

export function useBankDispatch() {
  return useContextSelector(BankMessagesDispatch, (dispatch) => {
    if (!dispatch)
      throw new Error(
        "useBankDispatch must be used within BankMessagesProvider"
      );
    return dispatch;
  });
}

export function useBitmask(): { [key: string]: boolean } {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.bitmask) || {};
}

export function usePositionMap(): { [key: string]: number } {
  return (
    useContextSelector(BankMessagesContext, (ctx) => ctx?.positionMap) || {}
  );
}

export function useServiceMap(): { [key: string]: string } {
  return (
    useContextSelector(BankMessagesContext, (ctx) => ctx?.serviceMap) || {}
  );
}

export function useDebounceMap(): Map<string, number> {
  return (
    useContextSelector(BankMessagesContext, (ctx) => ctx?.debounceMap) ||
    new Map()
  );
}

export function useClearBankMessages() {
  const dispatch = useBankDispatch();
  return () => dispatch({ type: "CLEAR_MESSAGES" });
}

export function useDirectionForLift(liftId: UuidT): DirectionEntry {
  const lk = liftKey(liftId);
  return (
    useContextSelector(DirectionContext, (ctx) => ctx?.[lk]) ?? {
      directionIcon: null,
      lastDirectionIcon: null,
    }
  );
}
